import {
  type CountryIdsInput,
  type DayOfWeekInput,
  type ExpressMembershipInput,
  type ExpressNonmemberLongTermHoldoutInput,
  type MatchesAnyRetailerIdInput,
  type MinimumPlatformVersionInput,
  type RetailerAvailabilityInput,
  type ShoppedAtRetailerInput,
  type ShoppedInput,
  type UserInteractionLimitInput,
  type WhitelistedPostalCodesInput,
  type WhitelistedZonesInput,
} from 'domains/banners/api'
import { type VisibilityConditionFormData } from 'domains/banners/types/visibilityCondition.types'

export const mapFormDataToCountryIdsInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): CountryIdsInput => {
  const countryIds = formData.conditionData.countryIds.countryIds
    .split(',')
    .map((countryId: string) => parseInt(countryId.trim(), 10))
  return {
    countryIds: countryIds,
    placementId: placementId,
  }
}

export const mapFormDataToDayOfWeekInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): DayOfWeekInput => {
  const days = formData.conditionData.dayOfWeek.days.split(',').map((day: string) => day.trim())
  return {
    days: days,
    placementId: placementId,
  }
}

export const mapFormDataToExpressMembershipInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): ExpressMembershipInput => {
  const membershipStatus =
    String(formData.conditionData.expressMembership.membershipStatus) === 'true'
  return {
    membershipStatus: membershipStatus,
    placementId: placementId,
  }
}

export const mapFormDataToExpressNonmemberLongTermHoldoutInput = (
  placementId: string,
  _formData: VisibilityConditionFormData
): ExpressNonmemberLongTermHoldoutInput => ({
  placementId: placementId,
})

export const mapFormDataToMatchesAnyRetailerIdInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): MatchesAnyRetailerIdInput => {
  const retailerIds = formData.conditionData.matchesAnyRetailerId.retailerIds
    .split(',')
    .map((retailerId: string) => parseInt(retailerId.trim(), 10))

  return {
    retailerIds: retailerIds,
    placementId: placementId,
  }
}

export const mapFormDataToMinimumPlatformVersionInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): MinimumPlatformVersionInput => ({
  platform: formData.conditionData.minimumPlatformVersion.platform,
  version: formData.conditionData.minimumPlatformVersion.version,
  placementId: placementId,
})

export const mapFormDataToRetailerAvailabilityInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): RetailerAvailabilityInput => ({
  retailerId: parseInt(formData.conditionData.retailerAvailability.retailerId, 10),
  placementId: placementId,
})

export const mapFormDataToShoppedAtRetailerInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): ShoppedAtRetailerInput => {
  const hasShopped = String(formData.conditionData.shoppedAtRetailer.hasShopped) === 'true'
  return {
    placementId: placementId,
    retailerId: parseInt(formData.conditionData.shoppedAtRetailer.retailerId, 10),
    hasShopped: hasShopped,
    numberOfDays: 1000,
    countryId: 814,
    storeConfigurationId: 1,
  }
}

export const mapFormDataToShoppedInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): ShoppedInput => {
  const hasShopped = String(formData.conditionData.shopped.hasShopped) === 'true'
  return {
    placementId: placementId,
    hasShopped: hasShopped,
    countryId: parseInt(formData.conditionData.shopped.countryId, 10),
    storeConfigurationId: parseInt(formData.conditionData.shopped.storeConfigurationId, 10),
  }
}

export const mapFormDataToUserInteractionLimitInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): UserInteractionLimitInput => ({
  placementId: placementId,
  maxImpressionsCount: parseInt(
    formData.conditionData.userInteractionLimit.maxImpressionsCount,
    10
  ),
  maxEngagementsCount: parseInt(
    formData.conditionData.userInteractionLimit.maxEngagementsCount,
    10
  ),
  maxDismissedCount: parseInt(formData.conditionData.userInteractionLimit.maxDismissedCount, 10),
  countCooldownSec: parseInt(formData.conditionData.userInteractionLimit.countCooldownSec, 10),
})

// Since all form values are stored as strings, map them to what the api expects
export const mapFormDataToWhitelistedPostalCodesInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): WhitelistedPostalCodesInput => {
  const postalCodes = formData.conditionData.whitelistedPostalCodes.postalCodes
    .split(',')
    .map((postalCode: string) => postalCode.trim())
  return {
    postalCodes: postalCodes,
    placementId: placementId,
  }
}

// Since all form values are stored as strings, map them to what the api expects
export const mapFormDataToWhitelistedZonesInput = (
  placementId: string,
  formData: VisibilityConditionFormData
): WhitelistedZonesInput => {
  const zones = formData.conditionData.whitelistedZones.zones
    .split(',')
    .map((zone: string) => zone.trim())
  return {
    zones: zones,
    placementId: placementId,
  }
}
